import { Button, CircularProgress, MenuItem, TextField } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApexTemplate } from '../../api/documentApi'
import { Client } from '../../redux/reducers/clients'
import { tokens } from '../../styles/tokens'

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  field: {
    flexGrow: 1,
    minWidth: '180px',
  },
  sectionTitle: {
    margin: 0,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
    fontWeight: 600,
    color: tokens.color.neutral[700],
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
}))

const TYP_FO = 'Fyzická osoba'
const TYP_PO = 'Právnická osoba'

interface Props {
  templates: ApexTemplate[]
  client: Partial<Client> | null
  loading: boolean
  onGenerate: (payload: { sablona: string; data: Record<string, any> }, format: 'pdf' | 'docx') => void
}

// Port formulare apex-generatoru (formular.html) — klice dat odpovidaji 1:1 API generatoru.
export const ApexDocumentForm: React.FC<Props> = ({ templates, client, loading, onGenerate }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [sablona, setSablona] = useState<string>('')
  const [data, setData] = useState<Record<string, string>>({
    TYP_UZIVATELE: TYP_FO,
    TELEFON: '+420 ',
    VSTUPNI_POPLATEK: '0',
    TYP_POPLATKU: 'Navýšení investice',
  })

  useEffect(() => {
    if (!client) return
    setData(prev => ({
      ...prev,
      TYP_UZIVATELE: client.company ? TYP_PO : TYP_FO,
      NAZEV_FIRMY: client.companyName || '',
      ICO: client.companyId || '',
      TITUL_PRED: client.titlePre || '',
      JMENO: client.firstName || '',
      PRIJMENI: client.surname || '',
      TITUL_ZA: client.titleAfter || '',
      RODNE_CISLO: client.personalNumber || '',
      ULICE: client.permanentAddress?.street || '',
      MESTO: client.permanentAddress?.town || '',
      PSC: client.permanentAddress?.zipCode || '',
      EMAIL: client.email || '',
      TELEFON: client.telephoneNumber || prev.TELEFON,
    }))
  }, [client])

  const setField = (name: string) => (event: React.ChangeEvent<{ value: string }>) => {
    setData(prev => ({ ...prev, [name]: event.target.value }))
  }

  const isCompany = data.TYP_UZIVATELE === TYP_PO

  const handleGenerate = (format: 'pdf' | 'docx') => () => {
    onGenerate({ sablona, data }, format)
  }

  const textField = (name: string, label: string, extra: any = {}) => (
    <TextField
      variant="standard"
      className={classes.field}
      label={label}
      value={data[name] || ''}
      onChange={setField(name)}
      {...extra}
    />
  )

  return (
    <div className={classes.form}>
      <div className={classes.row}>
        <TextField
          select
          variant="standard"
          className={classes.field}
          label="Typ dokumentu"
          value={sablona}
          onChange={event => setSablona(event.target.value)}
        >
          {templates.map(template => (
            <MenuItem key={template.path} value={template.path}>
              {template.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          variant="standard"
          className={classes.field}
          label={t('CLIENT')}
          value={data.TYP_UZIVATELE}
          onChange={setField('TYP_UZIVATELE')}
        >
          <MenuItem value={TYP_FO}>{TYP_FO}</MenuItem>
          <MenuItem value={TYP_PO}>{TYP_PO}</MenuItem>
        </TextField>
      </div>

      {isCompany && (
        <div className={classes.row}>
          {textField('NAZEV_FIRMY', 'Název firmy')}
          {textField('ICO', 'IČO')}
        </div>
      )}

      <div className={classes.row}>
        {textField('TITUL_PRED', 'Titul před')}
        {textField('JMENO', 'Jméno')}
        {textField('PRIJMENI', 'Příjmení')}
        {textField('TITUL_ZA', 'Titul za')}
      </div>

      {!isCompany && (
        <div className={classes.row}>
          {textField('RODNE_CISLO', 'Rodné číslo', { placeholder: 'RRMMDD/XXXX', inputProps: { maxLength: 11 } })}
        </div>
      )}

      <div className={classes.row}>
        {textField('ULICE', 'Ulice a číslo popisné')}
        {textField('MESTO', 'Město')}
        {textField('PSC', 'PSČ', { inputProps: { maxLength: 6 } })}
      </div>

      <div className={classes.row}>
        {textField('EMAIL', 'E-mail', { type: 'email' })}
        {textField('TELEFON', 'Telefon')}
      </div>

      <p className={classes.sectionTitle}>Investice</p>
      <div className={classes.row}>
        {textField('VYSE_INVESTICE', 'Výše investice (Kč)', { type: 'number' })}
        {textField('SPLATNOST', 'Splatnost', { type: 'date', InputLabelProps: { shrink: true } })}
        {textField('VSTUPNI_POPLATEK', 'Vstupní poplatek (%)', { type: 'number', inputProps: { step: 0.1 } })}
        <TextField
          select
          variant="standard"
          className={classes.field}
          label="Režim poplatku"
          value={data.TYP_POPLATKU}
          onChange={setField('TYP_POPLATKU')}
        >
          <MenuItem value="Navýšení investice">Přičíst k investici (Navýšení)</MenuItem>
          <MenuItem value="Stržení z investice">Strhnout z investice</MenuItem>
          <MenuItem value="Nulový">Žádný poplatek (0 Kč)</MenuItem>
        </TextField>
      </div>
      <div className={classes.row}>
        {textField('CISLO_UCTU', 'Číslo účtu pro výplatu', { placeholder: '123456789/0800' })}
      </div>

      <div className={classes.buttons}>
        <Button variant="contained" color="primary" disabled={loading || !sablona} onClick={handleGenerate('pdf')}>
          {t('DOWNLOAD_PDF')}
        </Button>
        <Button variant="outlined" color="primary" disabled={loading || !sablona} onClick={handleGenerate('docx')}>
          {t('DOWNLOAD_DOCX')}
        </Button>
        {loading && <CircularProgress size={24} />}
      </div>
    </div>
  )
}
